import { useState } from "react";
import Section from "../components/ui/Section";
import FuelLoading from "./FuelLoading"; // Reuses your rolling car animation
import "../styles/contact.scss";
import { useSubmitContactMessageMutation } from "../features/api/contactApiSlice";
import PageHeader from "../components/PageHeader/PageHeader";

export default function Contact() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [subject, setSubject] = useState("General Inquiry");
  const [message, setMessage] = useState("");
  const [honeypot, setHoneypot] = useState(""); // NEW: Track honeypot text string state

  const [submitContactMessage, { isLoading: isSubmitting }] =
    useSubmitContactMessageMutation();

  // Validation error states
  const [error, setError] = useState("");
  const [formErrors, setFormErrors] = useState({
    name: false,
    email: false,
    message: false,
  });

  const [isSuccess, setIsSuccess] = useState(false);

  // Clears input errors the moment the user types a keystroke
  const handleInputChange = (setterFn, fieldName) => (e) => {
    setterFn(e.target.value);
    if (error) setError("");
    if (formErrors[fieldName]) {
      setFormErrors((prev) => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // NEW: Fast-track trick the robot into thinking it succeeded without firing an API call
    if (honeypot.trim()) {
      setIsSuccess(true);
      return;
    }

    const newErrors = { name: false, email: false, message: false };
    let hasError = false;

    // 1. Name Check
    if (!name.trim()) {
      setError("Please tell us your name.");
      newErrors.name = true;
      hasError = true;
    }

    // 2. Email Check
    if (!email.trim()) {
      setError("Please enter your email address so we can reply.");
      newErrors.email = true;
      hasError = true;
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email.trim())) {
        setError("Please enter a valid email address.");
        newErrors.email = true;
        hasError = true;
      }
    }

    // 3. Message Check
    if (!message.trim()) {
      setError("Please type out a message note before submitting.");
      newErrors.message = true;
      hasError = true;
    }

    if (hasError) {
      setFormErrors(newErrors);
      return;
    }
    try {
      await submitContactMessage({
        name: name.trim(),
        email: email.trim(),
        subject,
        message: message.trim(),
        backdoorField: honeypot, // NEW: Send value to backend for double protection check
      }).unwrap();

      setIsSuccess(true);
    } catch (err) {
      setError(
        err?.data?.message ||
          "Unable to transmit message layers down the road. Try again!"
      );
    }
  };

  // Render Successful Message Card View
  if (isSuccess) {
    return (
      <main className="contact-page">
        <section className="contact-success-card">
          <div className="success-icon">📬 ✨ 🚗</div>
          <h1>Message Dispatched!</h1>
          <p>
            Thanks for reaching out, <strong>{name}</strong>! Your note has been
            sent down the road. We read every message and will get back to you
            soon.
          </p>
          <div className="success-badge">⚡ Route Logged Successfully</div>
          <button
            type="button"
            className="fuel-primary-btn"
            onClick={() => window.location.reload()}
          >
            Send Another Note
          </button>
        </section>
      </main>
    );
  }

  return (
    <main className="contact-page">
      <Section>
        <PageHeader
          title="Contact Us"
          subtitle="Drop us a line about route tips, partnerships, or just to say hello."
        />
        {isSubmitting ? (
          <div className="contact-loading-box">
            <FuelLoading text="Broadcasting message coordinates across state lines..." />
          </div>
        ) : (
          <form
            className="contact-card fuel-fade-in"
            onSubmit={handleSubmit}
            noValidate
          >
            <h2>Write a Note from the Road</h2>

            <div className="contact-form-group">
              {/* NEW: Honeypot Input wrapped in a special hidden utility class layout */}
              <div
                className="contact-input-wrapper contact-hidden-field"
                aria-hidden="true"
              >
                <label htmlFor="contact-hp">
                  Leave this empty if you are a human traveler
                </label>
                <input
                  id="contact-hp"
                  type="text"
                  name="backdoorField"
                  value={honeypot}
                  onChange={(e) => setHoneypot(e.target.value)}
                  tabIndex="-1"
                  autoComplete="off"
                />
              </div>
              {/* Name Input */}
              <div className="contact-input-wrapper">
                <label htmlFor="contact-name">
                  Your Name <span className="required-star">*</span>
                </label>
                <input
                  id="contact-name"
                  type="text"
                  className={formErrors.name ? "input-error" : ""}
                  value={name}
                  onChange={handleInputChange(setName, "name")}
                  placeholder="Adventurer Name"
                />
              </div>

              {/* Email Input */}
              <div className="contact-input-wrapper">
                <label htmlFor="contact-email">
                  Email Address <span className="required-star">*</span>
                </label>
                <input
                  id="contact-email"
                  type="email"
                  className={formErrors.email ? "input-error" : ""}
                  value={email}
                  onChange={handleInputChange(setEmail, "email")}
                  placeholder="driver@roadtrip.com"
                />
              </div>

              {/* Subject Dropdown Menu */}
              <div className="contact-input-wrapper">
                <label htmlFor="contact-subject">Topic / Subject</label>
                <select
                  id="contact-subject"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                >
                  <option value="General Inquiry">
                    👋 Say Hello / General Feedback
                  </option>
                  <option value="Route Recommendation">
                    🗺️ Route / Location Suggestion
                  </option>
                  <option value="Business/Partnership">
                    💼 Business & Partnership Idea
                  </option>
                  <option value="Gear Question">
                    ⚙️ Gear & Equipment Inquiry
                  </option>
                </select>
              </div>

              {/* Message Textarea */}
              <div className="contact-input-wrapper">
                <label htmlFor="contact-message">
                  Your Message <span className="required-star">*</span>
                </label>
                <textarea
                  id="contact-message"
                  className={formErrors.message ? "input-error" : ""}
                  value={message}
                  maxLength={1000}
                  onChange={handleInputChange(setMessage, "message")}
                  placeholder="Write your thoughts here..."
                />
                <div
                  className={`contact-counter ${
                    message.length >= 950 ? "limit-danger" : ""
                  }`}
                >
                  {message.length} / 1000 characters
                </div>
              </div>
            </div>

            {error && <div className="contact-error">{error}</div>}

            <button type="submit" className="fuel-primary-btn">
              Send Road Message
            </button>
          </form>
        )}
      </Section>
    </main>
  );
}
